import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useLanguage } from '../contexts/LanguageContext';

const { FiShield, FiLock, FiMail, FiPhone } = FiIcons;

const PrivacyPolicy = () => {
  const { t } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <section className="pt-24 pb-16 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-2xl shadow-xl p-8 md:p-12"
        >
          {/* Header */}
          <div className="flex items-center space-x-4 mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
              <SafeIcon icon={FiShield} className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">
                {t('privacy.title')}
              </h1>
              <p className="text-gray-600 mt-1">{t('privacy.lastUpdated')}</p>
            </div>
          </div>

          {/* Introduction */}
          <div className="mb-8">
            <p className="text-gray-700 leading-relaxed">
              {t('privacy.intro')}
            </p>
          </div>

          {/* Sections */}
          <div className="space-y-8">
            {/* Data Controller */}
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <SafeIcon icon={FiLock} className="w-6 h-6 text-blue-600" />
                <h2 className="text-2xl font-bold text-gray-900">
                  {t('privacy.controller.title')}
                </h2>
              </div>
              <div className="bg-gray-50 rounded-xl p-6 space-y-2">
                <p className="text-gray-700"><strong>{t('privacy.controller.company')}</strong> Bertoproduction</p>
                <p className="text-gray-700"><strong>{t('privacy.controller.nif')}</strong> Y/4514/312/R</p>
                <p className="text-gray-700"><strong>{t('privacy.controller.address')}</strong> La Manga del Mar Menor, San Javier 30380, España</p>
                <p className="text-gray-700 flex items-center">
                  <SafeIcon icon={FiMail} className="w-4 h-4 mr-2" />
                  <strong className="mr-2">{t('privacy.controller.email')}</strong>
                  <a href="mailto:info@tecnicoinformaticolamanga.com" className="text-blue-600 hover:underline">
                    info@tecnicoinformaticolamanga.com
                  </a>
                </p>
                <p className="text-gray-700 flex items-center">
                  <SafeIcon icon={FiPhone} className="w-4 h-4 mr-2" />
                  <strong className="mr-2">{t('privacy.controller.phone')}</strong>
                  <a href="tel:+34642643499" className="text-blue-600 hover:underline">
                    +34 642 643 499
                  </a>
                </p>
              </div>
            </div>

            {/* Data Collection */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('privacy.collection.title')}
              </h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-700 mb-3">{t('privacy.collection.intro')}</p>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>{t('privacy.collection.items.name')}</li>
                  <li>{t('privacy.collection.items.email')}</li>
                  <li>{t('privacy.collection.items.phone')}</li>
                  <li>{t('privacy.collection.items.service')}</li>
                  <li>{t('privacy.collection.items.message')}</li>
                </ul>
              </div>
            </div>

            {/* Purpose */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('privacy.purpose.title')}
              </h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>{t('privacy.purpose.items.respond')}</li>
                <li>{t('privacy.purpose.items.provide')}</li>
                <li>{t('privacy.purpose.items.improve')}</li>
                <li>{t('privacy.purpose.items.comply')}</li>
              </ul>
            </div>

            {/* Legal Basis */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('privacy.legal.title')}
              </h2>
              <p className="text-gray-700">{t('privacy.legal.description')}</p>
            </div>

            {/* Data Retention */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('privacy.retention.title')}
              </h2>
              <p className="text-gray-700">{t('privacy.retention.description')}</p>
            </div>

            {/* Cookies */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('privacy.cookies.title')}
              </h2>
              <p className="text-gray-700 mb-3">{t('privacy.cookies.description')}</p>
              <div className="space-y-3">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    {t('privacy.cookies.necessary.title')}
                  </h3>
                  <p className="text-sm text-gray-700">
                    {t('privacy.cookies.necessary.description')}
                  </p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    {t('privacy.cookies.analytics.title')}
                  </h3>
                  <p className="text-sm text-gray-700">
                    {t('privacy.cookies.analytics.description')}
                  </p>
                </div>
              </div>
            </div>

            {/* User Rights */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('privacy.rights.title')}
              </h2>
              <p className="text-gray-700 mb-3">{t('privacy.rights.intro')}</p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li><strong>{t('privacy.rights.items.access.title')}</strong> {t('privacy.rights.items.access.description')}</li>
                <li><strong>{t('privacy.rights.items.rectification.title')}</strong> {t('privacy.rights.items.rectification.description')}</li>
                <li><strong>{t('privacy.rights.items.erasure.title')}</strong> {t('privacy.rights.items.erasure.description')}</li>
                <li><strong>{t('privacy.rights.items.restriction.title')}</strong> {t('privacy.rights.items.restriction.description')}</li>
                <li><strong>{t('privacy.rights.items.portability.title')}</strong> {t('privacy.rights.items.portability.description')}</li>
                <li><strong>{t('privacy.rights.items.objection.title')}</strong> {t('privacy.rights.items.objection.description')}</li>
              </ul>
              <p className="text-gray-700 mt-3">{t('privacy.rights.exercise')}</p>
            </div>

            {/* Security */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('privacy.security.title')}
              </h2>
              <p className="text-gray-700">{t('privacy.security.description')}</p>
            </div>

            {/* Contact */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('privacy.contact.title')}
              </h2>
              <p className="text-gray-700 mb-4">{t('privacy.contact.description')}</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="mailto:info@tecnicoinformaticolamanga.com"
                  className="inline-flex items-center justify-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                >
                  <SafeIcon icon={FiMail} className="w-5 h-5" />
                  <span>{t('privacy.contact.email')}</span>
                </a>
                <a
                  href="tel:+34642643499"
                  className="inline-flex items-center justify-center space-x-2 bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
                >
                  <SafeIcon icon={FiPhone} className="w-5 h-5" />
                  <span>{t('privacy.contact.phone')}</span>
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PrivacyPolicy;